// Acompanhamento da assinatura de um documento de VENDA (2026-08-21).
//
// Substitui o D4SignStatusBox, que lia colunas `d4sign_*` do documento e se
// atualizava por POLLING de 30s — 120 chamadas por hora à API da D4 Sign por
// documento aberto, contra uma cota padrão de 10/hora. Aqui a leitura é do
// NOSSO banco: quem fala com a D4 Sign é o cron de 15 minutos, e o botão
// "Verificar agora" existe para quem acabou de assinar e quer ver na hora.
//
// A caixa some quando nunca houve envio — quem nunca mandou não precisa ver
// espaço reservado para um estado que não existe.

import { useCallback, useEffect, useState } from 'react';
import { App as AntApp, Button, Flex, Popconfirm, Space, Tag, Typography } from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  FilePdfOutlined, ReloadOutlined, WarningOutlined,
} from '@ant-design/icons';
import {
  statusAssinaturaVenda, verificarAssinaturaVenda,
  cancelarAssinaturaVenda, pdfAssinadoVenda, retomarAssinaturaVenda, trocarSignatarioVenda,
} from './apiAssinatura';
import { Envelope, ENVELOPE_VIVO, ROTULO_STATUS } from '../../components/assinatura/tipos';
import { avisoDoEnvio } from '../../components/assinatura/avisoEnvio';
import ModalTrocarSignatario from '../../components/assinatura/ModalTrocarSignatario';

const { Text } = Typography;

const CORES: Record<Envelope['status'], string> = {
  rascunho: 'default', processando_envio: 'processing',
  aguardando_assinatura: 'gold', assinado: 'green',
  cancelado: 'default', erro_envio: 'red', erro_consulta: 'orange',
};

const ICONES: Partial<Record<Envelope['status'], React.ReactNode>> = {
  processando_envio: <ClockCircleOutlined />,
  aguardando_assinatura: <ClockCircleOutlined />,
  assinado: <CheckCircleOutlined />,
  cancelado: <CloseCircleOutlined />,
  erro_envio: <WarningOutlined />,
};

const dataBr = (v: string | null) => (v ? new Date(v).toLocaleString('pt-BR') : null);

export default function CaixaAssinatura({ docId, recarregar }: {
  docId: string;
  /** Chamado quando o estado muda, para a tela de cima recarregar o documento. */
  recarregar?: () => void;
}) {
  const { message } = AntApp.useApp();
  const [env, setEnv] = useState<Envelope | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [verificando, setVerificando] = useState(false);
  const [trocaAberta, setTrocaAberta] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await statusAssinaturaVenda(docId);
      setEnv(r.envelope);
    } catch { /* sem envelope é estado válido — a caixa só não aparece */ }
    finally { setCarregando(false); }
  }, [docId]);

  useEffect(() => { carregar(); }, [carregar]);

  if (carregando || !env) return null;

  const assinados = (env.signatarios || []).filter(s => s.status === 'assinado').length;
  const total = (env.signatarios || []).length;
  const vivo = ENVELOPE_VIVO.includes(env.status);

  async function verificarAgora() {
    setVerificando(true);
    try {
      const r = await verificarAssinaturaVenda(docId);
      setEnv(r.envelope);
      recarregar?.();
      message.success(r.envelope?.status === 'assinado'
        ? 'Assinado!' : 'Consultei a D4 Sign — ainda não há assinatura nova.');
    } catch (e: any) {
      // 429 (carência de 45s) e 502 (D4 Sign fora) chegam com texto próprio —
      // mostrar o texto do backend é melhor que "erro ao verificar".
      message.error(e.response?.data?.error || e.message, 8);
    } finally { setVerificando(false); }
  }

  return (
    <div style={{
      border: '1px solid #e3e5ea', borderRadius: 8, padding: '10px 12px',
      background: '#fafbfc', marginTop: 12,
    }}>
      <Flex align="center" gap={8} wrap style={{ marginBottom: total ? 8 : 0 }}>
        <Text strong style={{ fontSize: 13 }}>Assinatura digital</Text>
        <Tag color={CORES[env.status]} icon={ICONES[env.status]}>
          {ROTULO_STATUS[env.status]}
        </Tag>
        {total > 0 && env.status !== 'cancelado' && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {assinados} de {total} assinaram
          </Text>
        )}
        {env.status === 'assinado' && dataBr(env.assinado_em) && (
          <Text type="secondary" style={{ fontSize: 12 }}>em {dataBr(env.assinado_em)}</Text>
        )}
        {vivo && dataBr(env.enviado_em) && (
          <Text type="secondary" style={{ fontSize: 12 }}>enviado em {dataBr(env.enviado_em)}</Text>
        )}
      </Flex>

      {total > 0 && (
        <Space direction="vertical" size={2} style={{ width: '100%', marginBottom: 8 }}>
          {env.signatarios.map(s => (
            <Flex key={s.email} align="center" gap={8} wrap>
              <Text style={{ fontSize: 12 }}>{s.nome}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{s.email}</Text>
              {s.qualificacao && <Tag style={{ fontSize: 10 }}>{s.qualificacao}</Tag>}
              <Tag color={s.status === 'assinado' ? 'green' : 'default'} style={{ fontSize: 10 }}>
                {s.status === 'assinado' ? 'assinou' : 'pendente'}
              </Tag>
              {s.assinado_em && (
                <Text type="secondary" style={{ fontSize: 10 }}>{dataBr(s.assinado_em)}</Text>
              )}
            </Flex>
          ))}
        </Space>
      )}

      {/* Na recusa por COTA não há o que corrigir — o conselho antigo
          ("corrija e envie de novo") levava a tentar de novo na hora, o que
          consome o mesmo limite e atrasa a liberação (30/09/2026). */}
      {(() => {
        const aviso = avisoDoEnvio(env);
        if (!aviso) return null;
        return (
          <Text type={aviso.tipo === 'error' ? 'danger' : aviso.tipo === 'warning' ? 'warning' : 'secondary'}
            style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            <strong>{aviso.titulo}.</strong> {aviso.descricao}
          </Text>
        );
      })()}
      {/* Assinado sem o arquivo de volta: sem esta linha, a caixa mostrava o
          botão de baixar e o clique dava erro. */}
      {env.aguardando_arquivo && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Assinado — a D4 Sign ainda não entregou o PDF assinado. O sistema busca
          sozinho de novo
          {env.arquivo_previsto_em
            ? ` a partir das ${new Date(env.arquivo_previsto_em)
                .toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
            : ' em até uma hora'}; não é preciso assinar de novo.
        </Text>
      )}
      {env.status === 'cancelado' && env.cancelado_motivo && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Motivo: {env.cancelado_motivo}
        </Text>
      )}

      <Space wrap>
        {env.status === 'assinado' && env.pdf_assinado_s3_key && (
          <Button size="small" type="primary" icon={<FilePdfOutlined />}
            onClick={async () => {
              try {
                const r = await pdfAssinadoVenda(docId);
                window.open(r.url, '_blank');
              } catch (e: any) { message.error(e.response?.data?.error || e.message, 8); }
            }}>
            Baixar assinado
          </Button>
        )}
        {vivo && (
          <Button size="small" icon={<ReloadOutlined />} loading={verificando}
            onClick={verificarAgora}>
            Verificar agora
          </Button>
        )}
        {/* O contato do cliente muda depois do envio com mais frequência do
            que se imagina — foi o caso da VND-2026-087 em 04/10/2026. */}
        {env.status === 'aguardando_assinatura'
          && env.signatarios.some(s => s.status !== 'assinado') && (
          <Button size="small" onClick={() => setTrocaAberta(true)}>
            Trocar quem assina
          </Button>
        )}
        {vivo && (
          <Popconfirm
            title="Cancelar o envio?"
            description="O documento deixa de ser assinável na D4 Sign. É o que permite reenviar."
            okText="Cancelar envio" cancelText="Voltar"
            onConfirm={async () => {
              try {
                await cancelarAssinaturaVenda(docId, 'Cancelado pelo usuário');
                message.success('Envio cancelado');
                await carregar(); recarregar?.();
              } catch (e: any) { message.error(e.response?.data?.error || e.message, 8); }
            }}>
            <Button size="small" danger>Cancelar envio</Button>
          </Popconfirm>
        )}
        {avisoDoEnvio(env)?.acao === 'retomar' && (
          <Button size="small" type="primary"
            onClick={async () => {
              try {
                await retomarAssinaturaVenda(docId);
                message.success('Retomando o envio — os signatários serão avisados');
                await carregar(); recarregar?.();
              } catch (e: any) { message.error(e.response?.data?.error || e.message, 8); }
            }}>
            Retomar envio
          </Button>
        )}
        {env.status === 'erro_envio' && env.retentativa?.no_cofre && (
          <Popconfirm
            title="Cancelar a via que ficou no cofre?"
            description="O documento deixa de ser assinável na D4 Sign. É o que libera mandar outro."
            okText="Cancelar a via" cancelText="Voltar"
            onConfirm={async () => {
              try {
                await cancelarAssinaturaVenda(docId, 'Via que ficou no cofre após falha no envio');
                message.success('Via cancelada na D4 Sign');
                await carregar(); recarregar?.();
              } catch (e: any) { message.error(e.response?.data?.error || e.message, 8); }
            }}>
            <Button size="small" danger>Cancelar a via do cofre</Button>
          </Popconfirm>
        )}
        {/* ⚠️ "Pode enviar de novo" some quando o documento está no cofre: ali
            o reenvio cria uma SEGUNDA via assinável, e o servidor recusa. */}
        {!vivo && env.status !== 'assinado' && avisoDoEnvio(env)?.acao !== 'retomar'
          && !env.retentativa?.no_cofre && !env.retentativa?.prevista_em && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Você pode enviar de novo pelo botão "Enviar para assinatura".
          </Text>
        )}
        {vivo && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            O sistema confere sozinho a cada 15 minutos — não precisa deixar a tela aberta.
          </Text>
        )}
      </Space>

      <ModalTrocarSignatario
        aberto={trocaAberta}
        envelope={env}
        trocar={(p) => trocarSignatarioVenda(docId, p)}
        aoFechar={() => setTrocaAberta(false)}
        aoTrocar={async () => { await carregar(); recarregar?.(); }}
      />
    </div>
  );
}
